import { Box, Stack, Typography } from "@mui/material";
import { KeyRoleMapper } from "../KeyRoleMapper";
import { TriangleIcon } from "../../images";
import { RowWrapper } from "./RowWrapper";
import { RoleMembers, RoleMembersItem } from "../../../types/apiTypes";
import { CSSProperties, useEffect, useState } from "react";
import { RoleItemMember } from "./RoleItemMember";
import { MembersUpdateValues } from "../../../types/companyTypes";
import { ReactSortable, SortableEvent } from "react-sortablejs";
import { KeyRoles } from "../../../types/enums";
import { GetContactsForRoleBtn, ToTopBtn } from "../../ui/buttons";
import {
  useAppDispatch,
  useAppSelector,
  useStateWithSearchParams,
} from "../../../hooks";
import { setDiscardTableChanges } from "../../../features/companySlice";
import { useGetCampaignCurrentRoleMembersQuery } from "../../../api";
import { EmptyRoleRow } from "./EmptyRoleRow";
import { keyRolesItems } from "../../../types/genereal";
import { RoleTooltip } from "../RoleTooltip";
import { ReuseTooltip } from "../../ui";

const style: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  flexWrap: "nowrap",
};

const sortableOptions = {
  animation: 150,
  fallbackOnBody: true,
  swapThreshold: 0.65,
  ghostClass: "ghost",
  forceFallback: true,
  style,
};

const listOptions = {
  ...sortableOptions,
  group: { name: "shared", pull: true, put: true },
  filter: ".no-drag",
};

type Props = RoleMembers & {
  account_id: string;
  isEditMode: boolean;
  updatedValues: {
    [key: string]: MembersUpdateValues;
  };
  updateHandler: (
    values?: MembersUpdateValues,
    allValues?: {
      [key: string]: MembersUpdateValues;
    },
  ) => void;
  setCommitteeSelected: React.Dispatch<React.SetStateAction<number>>;
  setClusterSelected: React.Dispatch<React.SetStateAction<number>>;
  currentScraping?: KeyRoles;
};

type SearchParams = {
  sort?: string;
  min?: string;
  max?: string;
};

export const RoleItem = (props: Props) => {
  const {
    id,
    items,
    total,
    scraping,
    isEditMode,
    updatedValues,
    updateHandler,
    setCommitteeSelected,
    setClusterSelected,
    account_id,
    is_active,
    status,
    currentScraping,
  } = props;
  const isNotInvolved = id === KeyRoles.not_involved;
  const dispatch = useAppDispatch();
  const isDiscardChanges = useAppSelector(
    (state) => state.company.discardTableChanges,
  );
  const { isEditor } = useAppSelector((state) => state.company.userRoles);

  const [list, setList] = useState<RoleMembersItem[]>(items);
  const [page, setPage] = useState<number>(0);
  const [isShowMore, setIsShowMore] = useState<boolean>(false);
  const [showScrollTop, setShowScrollTop] = useState<boolean>(false);
  const [isScroll, setIsScroll] = useState<boolean>(false);
  const [totalMembers, setTotalMembers] = useState<number>(0);

  const canLoadMore = total > totalMembers;

  useEffect(() => {
    if (isNotInvolved) {
      const el = document.getElementById(id);
      if (el) {
        const handleScroll = () => {
          const { top, height } = el.getBoundingClientRect();

          setShowScrollTop(top <= -200);
          setIsScroll(top <= -height + 4000);
        };

        window.addEventListener("scroll", handleScroll);

        return () => window.removeEventListener("scroll", handleScroll);
      }
    }
  }, []);

  useEffect(() => {
    setList(items);
    setTotalMembers(items.length);
    setPage(0);
  }, [items]);

  const { searchDict } = useStateWithSearchParams<SearchParams>({
    sort: undefined,
    min: undefined,
    max: undefined,
  });

  const { data, isFetching } = useGetCampaignCurrentRoleMembersQuery(
    {
      account_id,
      page,
      per_page: 100,
      role_id: id,
      min_percent: searchDict.min ? Number(searchDict.min) : undefined,
      max_percent: searchDict.max ? Number(searchDict.max) : undefined,
    },
    { refetchOnMountOrArgChange: true, skip: !isNotInvolved || page === 0 },
  );

  useEffect(() => {
    if (isScroll && isShowMore && canLoadMore && !isFetching) {
      setPage((prev) => prev + 1);
      setIsScroll(false);
    }
  }, [isScroll]);

  useEffect(() => {
    if (data && isNotInvolved) {
      if (page > 0) {
        setList((prev) => [...prev, ...data.items]);
        setTotalMembers((prev) => prev + data.items.length);
      }
    }
  }, [data]);

  useEffect(() => {
    if (isDiscardChanges) {
      setList(items);
      setTotalMembers(items.length);
      dispatch(setDiscardTableChanges(false));
    }
  }, [isDiscardChanges]);

  const onAdd = (e: SortableEvent) => {
    const member_id = e.item.getAttribute("data-id");
    if (!member_id || e.newIndex === undefined) return;

    const values = { ...updatedValues };

    if (!updatedValues[member_id]) {
      values[member_id] = {
        member_id,
        role: e.to.id as KeyRoles,
        custom_index: e.newIndex,
        original_index: e.oldIndex,
      };
    }

    const updatedMembers: {
      [key: string]: MembersUpdateValues;
    } = Object.values(values).reduce(
      (
        acc: {
          [key: string]: MembersUpdateValues;
        },
        el,
      ) => {
        if (el.member_id === member_id) {
          acc[el.member_id] = {
            ...el,
            custom_index: e.newIndex,
            role: e.to.id as KeyRoles,
          };
          return acc;
        }

        if (e.from.id === el.role) {
          acc[el.member_id] = {
            ...el,
            custom_index:
              el.custom_index! > e.oldIndex!
                ? el.custom_index! - 1
                : el.custom_index!,
          };
          return acc;
        }

        if (e.to.id === el.role) {
          acc[el.member_id] = {
            ...el,
            custom_index:
              el.custom_index! >= e.newIndex!
                ? el.custom_index! + 1
                : el.custom_index!,
          };
          return acc;
        }

        acc[el.member_id] = el;
        return acc;
      },
      {},
    );

    updateHandler(undefined, updatedMembers);
  };

  const onUpdate = (e: SortableEvent) => {
    const member_id = e.item.getAttribute("data-id");
    if (!member_id || e.newIndex === e.oldIndex || e.newIndex === undefined)
      return;

    const values = { ...updatedValues };

    if (!updatedValues[member_id]) {
      values[member_id] = {
        member_id,
        role: e.from.id as KeyRoles,
        custom_index: e.newIndex,
        original_index: e.oldIndex,
      };
    }

    const updatedMembers: {
      [key: string]: MembersUpdateValues;
    } = Object.values(values).reduce(
      (
        acc: {
          [key: string]: MembersUpdateValues;
        },
        el,
      ) => {
        if (el.member_id === member_id) {
          acc[el.member_id] = { ...el, custom_index: e.newIndex };
          return acc;
        }

        if (e.from.id === el.role) {
          acc[el.member_id] = {
            ...el,
            custom_index:
              el.custom_index! >= e.newIndex! && e.oldIndex! >= el.custom_index!
                ? el.custom_index! + 1
                : el.custom_index! <= e.newIndex! &&
                    e.oldIndex! <= el.custom_index!
                  ? el.custom_index! - 1
                  : el.custom_index!,
          };
          return acc;
        }

        acc[el.member_id] = el;
        return acc;
      },
      {},
    );

    updateHandler(undefined, updatedMembers);
  };

  return (
    <RowWrapper key={id}>
      <Stack
        maxWidth={"200px"}
        minWidth={"200px"}
        gap="16px"
        paddingTop="2px"
        alignItems="flex-start"
      >
        <Box maxWidth="150px">
          <ReuseTooltip
            text={
              <RoleTooltip
                tooltip={keyRolesItems[id].tooltip}
                activeStatus={status}
              />
            }
            hideChildren={false}
          >
            <KeyRoleMapper item={id} roleStatus={status} />
          </ReuseTooltip>
        </Box>

        {list.length > 3 && (
          <Stack
            sx={{
              cursor: "pointer",
              paddingLeft: "4px",
              alignItems: "center",
              gap: "12px",
              flexDirection: "row",
            }}
            onClick={() => setIsShowMore(!isShowMore)}
          >
            <TriangleIcon rotate={isShowMore ? "180" : "0"} />
            <span
              style={{
                fontSize: "14px",
                fontWeight: 550,
                lineHeight: "20px",
                color: "#1C3C6C",
              }}
            >
              {isShowMore ? "Hide Contacts" : "Show All Contacts"}
            </span>
          </Stack>
        )}
      </Stack>
      <Stack flexGrow={1} gap="8px" data-role={id} flexWrap="nowrap">
        {list.length === 0 && !isEditMode && (
          <Stack
            className="no-drag"
            sx={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              padding: "16px 0",
              border: "1px solid #E0E0E0",
              borderRadius: "8px",
              width: "100%",
            }}
          >
            {isEditor ? (
              <GetContactsForRoleBtn
                account_id={account_id}
                role={id}
                isLoading={currentScraping?.includes(id) || !!scraping}
                isDisabled={
                  [id, "scraping", "update"].some((el) =>
                    currentScraping?.includes(el),
                  ) ||
                  !!scraping ||
                  !is_active
                }
              />
            ) : (
              <Stack justifyContent="center" alignItems="center">
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: "12px",
                    lineHeight: "18px",
                    color: "#464646",
                  }}
                >
                  No contacts have been added for this role yet.
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: "12px",
                    lineHeight: "18px",
                    color: "#464646",
                  }}
                >
                  This can be done by someone with edit access to the campaign.
                </Typography>
              </Stack>
            )}
          </Stack>
        )}
        <ReactSortable
          list={list.map((el, index) => ({ ...el, index, chosen: true }))}
          setList={setList}
          {...listOptions}
          onUpdate={onUpdate}
          onAdd={onAdd}
          id={id}
        >
          {list.slice(0, isShowMore ? undefined : 3).map((member) => (
            <RoleItemMember
              key={member.id}
              isEditMode={isEditMode}
              updatedValues={updatedValues[member.id]}
              updateHandler={updateHandler}
              setCommitteeSelected={setCommitteeSelected}
              setClusterSelected={setClusterSelected}
              {...member}
            />
          ))}
        </ReactSortable>
        {isShowMore && isNotInvolved && (
          <>
            {canLoadMore && (
              <>
                {[1, 2, 3].map((el) => (
                  <EmptyRoleRow key={el} />
                ))}
              </>
            )}

            {showScrollTop && <ToTopBtn />}
          </>
        )}
      </Stack>
    </RowWrapper>
  );
};
